import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
  IconButton,
  Avatar,
  Divider,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { ListChecks, ArrowRight, Check, X } from '@phosphor-icons/react';
import { Api } from '../api';
import { useNavigate } from 'react-router';
import { ScreenError, SoloLoading } from '.';
import { Model } from '../models';
import {
  addDays,
  format,
  startOfDay,
  parseISO,
  isAfter,
  isBefore,
} from 'date-fns';

/**
 * The member's programs (drafts and coming-soon programs aren't shown). Started ones open
 * their week-by-week progress; the one running now also shows this week at a glance.
 */
export default function UserProgramSection({
  userStats,
}: {
  userStats: Api.UserStats.V;
}) {
  const userId = userStats.user.id;
  const programsApi = Api.UserProgram.useReadCollection(userId);
  const navigate = useNavigate();

  const handleViewProgramProgress = (programId: string, section?: string) => {
    const hash = section ? `#${section}` : '';
    navigate(`/users/${userId}/program/${programId}${hash}`);
  };

  const programs = (programsApi.data?.member ?? []).filter(
    (p) => p.state !== 'coming_soon',
  );

  return (
    <Card>
      <CardHeader
        avatar={
          <Avatar>
            <ListChecks size={24} />
          </Avatar>
        }
        title="Program"
      />
      <CardContent>
        {programsApi.error ? (
          <ScreenError {...programsApi} />
        ) : !programsApi.data ? (
          <SoloLoading />
        ) : (
          <Stack divider={<Divider flexItem />} spacing={2}>
            {programs.map((program) => (
              <Stack key={program.id} spacing={2}>
                <ProgramRow
                  program={program}
                  onViewProgramProgress={(section) =>
                    handleViewProgramProgress(program.id, section)
                  }
                />
                {program.state === 'current' &&
                  program.progress &&
                  ['in_progress', 'paused'].includes(
                    program.progress.status,
                  ) && (
                    <ProgramWeekOverview
                      userId={userId}
                      program={program}
                      onViewProgramProgress={(section) =>
                        handleViewProgramProgress(program.id, section)
                      }
                    />
                  )}
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function ProgramRow({
  program,
  onViewProgramProgress,
}: {
  program: Api.UserProgram.V;
  onViewProgramProgress: (section?: string) => void;
}) {
  const state = Model.Program.PROGRAM_STATE_OPTIONS.find(
    (o) => o.value === program.state,
  );
  const formatDate = (d: string) => format(parseISO(d), 'dd MMM yyyy');
  const { progress } = program;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Program {program.number}
          </Typography>
          <Typography variant="subtitle1">{program.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {program.totalWeeks} weeks
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            size="small"
            label={state?.label ?? program.state}
            color={state?.color ?? 'default'}
          />
          {progress && progress.status !== 'finished' && (
            <Chip
              size="small"
              variant="outlined"
              label={Model.ProgramProgress.getStatusLabel(progress)}
              color={Model.ProgramProgress.getStatusColor(progress)}
            />
          )}
          {progress?.currentWeekNumber &&
            ['in_progress', 'paused'].includes(progress.status) && (
              <Typography variant="body2">
                Week {progress.currentWeekNumber} of {program.totalWeeks}
              </Typography>
            )}
          {program.startDate && program.endDate && (
            <Typography variant="body2" color="text.secondary">
              {formatDate(program.startDate)} – {formatDate(program.endDate)}
            </Typography>
          )}
        </Stack>
        {program.lockReasons.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {program.lockReasons
              .map((r) => Model.Program.PROGRAM_LOCK_REASON_LABELS[r] ?? r)
              .join(' · ')}
            {program.unlocksAt && ` · unlocks ${formatDate(program.unlocksAt)}`}
          </Typography>
        )}
      </Stack>
      {program.participationId &&
        program.progress?.status !== 'not_started' && (
          <Button
            variant="text"
            endIcon={<ArrowRight />}
            onClick={() => onViewProgramProgress('all')}
          >
            Program Progress
          </Button>
        )}
    </Stack>
  );
}

function ProgramWeekOverview({
  userId,
  program,
  onViewProgramProgress,
}: {
  userId: string;
  program: Api.UserProgram.V;
  onViewProgramProgress: (section?: string) => void;
}) {
  const weekNumber = program.progress?.currentWeekNumber ?? 1;
  const today = new Date();
  const startOfToday = startOfDay(today);

  const weekApi = Api.ProgramWeek.useReadItemByProgram(
    userId,
    program.id,
    String(weekNumber),
  );
  const week = weekApi.data;

  if (weekApi.error) return <ScreenError {...weekApi} />;
  if (!week) return <SoloLoading />;

  const weekStartDate = week.startDate
    ? parseISO(week.startDate)
    : startOfToday;
  const weekEndDate = week.endDate
    ? parseISO(week.endDate)
    : addDays(weekStartDate, 6);
  const { lessons, prompts, tasks, dailyTips, reflectionPoints } = week;

  const tipsViewed = dailyTips.filter((t) => t.isViewed || t.isSaved).length;
  const lessonsCompleted = lessons.filter((l) => l.isCompleted).length;

  const lessonsWithQuestionCount = lessons.filter((lesson) =>
    lesson.slides.some((s) => {
      if (s.templateName === 'text-image') {
        return Boolean((s.templateData as any)[4]);
      }
      return s.templateName === 'text' && Boolean((s.templateData as any)[3]);
    }),
  ).length;

  const lessonsAnswered = lessons.filter((l) => l.userAnswer).length;
  const promptsAnswered = prompts?.filter((p) => Boolean(p.userAnswer)).length;

  const answeredReflectionPoints = reflectionPoints.filter(
    (rp) => rp.userAnswer?.answer,
  );

  return (
    <Stack direction="row" spacing={2}>
      <Box
        sx={{
          bgcolor: 'var(--mui-palette-background-level1)',
          borderRadius: 2,
          p: 2,
          minHeight: 200,
          cursor: 'pointer',
        }}
        onClick={() => onViewProgramProgress('lessons')}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <PieChart
              series={[
                {
                  data: [
                    {
                      id: 0,
                      value: lessonsCompleted ?? 0,
                      color: '#2D556C',
                    },
                    {
                      id: 1,
                      value: Math.max(
                        (lessons?.length ?? 0) - (lessonsCompleted ?? 0),
                        0,
                      ),
                      color: '#BDC7CC',
                    },
                  ],
                  innerRadius: 60,
                  outerRadius: 80,
                  paddingAngle: 0,
                  cornerRadius: 0,
                },
              ]}
              width={160}
              height={160}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle1">Lessons</Typography>
              <Typography variant="body1">{`${lessonsCompleted}/${lessons?.length ?? 0}`}</Typography>
              <Typography variant="caption" color="text.secondary">
                completed
              </Typography>
            </Box>
          </Box>

          <Stack spacing={1} alignItems="center">
            <Typography variant="overline" color="text.secondary">
              QUESTIONS
            </Typography>
            <Chip
              label={`${lessonsAnswered} response${lessonsAnswered === 1 ? '' : 's'}`}
              variant="filled"
              color="primary"
              deleteIcon={<ArrowRight size={16} />}
              onDelete={() => onViewProgramProgress('lessons')}
            />
            <Typography variant="caption" color="text.secondary">
              {lessonsWithQuestionCount} total this week
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          bgcolor: 'var(--mui-palette-background-level1)',
          borderRadius: 2,
          p: 2,
          minHeight: 200,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1">Tasks of the week</Typography>
          <Stack spacing={2}>
            {tasks.slice(0, 3).map((task) => {
              const completedDates = new Set(
                (task.completedDates || []).map((d) => {
                  const date = parseISO(d);
                  const dow = (date.getUTCDay() + 6) % 7; // Mon=0..Sun=6
                  return dow;
                }),
              );
              const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
              return (
                <Stack key={task['@id']} spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {task.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    {days.map((label, idx) => {
                      const done = completedDates.has(idx);
                      const dayDate = addDays(weekStartDate, idx);
                      const isPast = isBefore(dayDate, startOfToday);
                      const missed = !done && isPast;

                      const labelColor = done
                        ? 'success.main'
                        : missed
                          ? 'error.main'
                          : 'text.secondary';

                      return (
                        <Stack
                          key={label}
                          alignItems="center"
                          spacing={0.5}
                          sx={{ flex: 1 }}
                        >
                          <Typography variant="subtitle2" color={labelColor}>
                            {label}
                          </Typography>
                          {done ? (
                            <Check
                              size={16}
                              color="var(--mui-palette-success-main)"
                            />
                          ) : missed ? (
                            <X
                              size={16}
                              color="var(--mui-palette-error-main)"
                            />
                          ) : (
                            <Box sx={{ width: 16, height: 16 }} />
                          )}
                        </Stack>
                      );
                    })}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          bgcolor: 'var(--mui-palette-background-level1)',
          borderRadius: 2,
          p: 2,
          minHeight: 200,
        }}
      >
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <PieChart
              series={[
                {
                  data: [
                    { id: 0, value: tipsViewed, color: '#2D556C' },
                    {
                      id: 1,
                      value: Math.max(dailyTips.length - tipsViewed, 0),
                      color: '#BDC7CC',
                    },
                  ],
                  innerRadius: 60,
                  outerRadius: 80,
                  paddingAngle: 0,
                  cornerRadius: 0,
                },
              ]}
              width={160}
              height={160}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography variant="subtitle1">Daily Tips</Typography>
              <Typography variant="body1">{`${tipsViewed}/${dailyTips.length ?? 0}`}</Typography>
              <Typography variant="caption" color="text.secondary">
                viewed
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Box>

      <Stack spacing={2} sx={{ minHeight: 200 }}>
        <Box
          sx={{
            bgcolor: 'var(--mui-palette-background-level1)',
            borderRadius: 2,
            p: 2,
            flex: 1,
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">Psychology Prompts</Typography>
              <IconButton
                size="small"
                onClick={() => onViewProgramProgress('psychology-prompts')}
              >
                <ArrowRight size={16} />
              </IconButton>
            </Stack>
            <Chip
              label={`${promptsAnswered}/${prompts.length ?? 0} answered`}
              color="success"
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            />
          </Stack>
        </Box>

        <Box
          sx={{
            bgcolor: 'var(--mui-palette-background-level1)',
            borderRadius: 2,
            p: 2,
            flex: 1,
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">Reflection Points</Typography>
              <IconButton
                size="small"
                onClick={() => onViewProgramProgress('reflection-points')}
              >
                <ArrowRight size={16} />
              </IconButton>
            </Stack>
            {isAfter(weekEndDate, startOfToday) &&
            answeredReflectionPoints.length === 0 ? (
              <Typography
                variant="subtitle2"
                color="text.disabled"
                sx={{ maxWidth: 200 }}
              >
                not visible to the user yet, appears at the end of the week
              </Typography>
            ) : (
              <Chip
                label={`${answeredReflectionPoints.length}/${reflectionPoints.length ?? 0} answered`}
                color="success"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
}
